import type { ReportElementType } from '@highimpact-frontend/api-types';

import { flattenExportableElements } from './flattenExportableElements';
import { resolveReportExportDisplayName } from './resolveReportExportDisplayName';
import type {
  CreateWidgetInstanceRegistryOptions,
  ReportExportMode,
  ReportGatherOptions,
  ReportWidgetExportResult,
  WidgetInstanceCapabilities,
  WidgetInstanceRegistry,
} from './types';

/** Create a widget instance registry that centralizes the capabilities of all widgets. */
export function createWidgetInstanceRegistry(
  options: CreateWidgetInstanceRegistryOptions = {}
): WidgetInstanceRegistry {
  const { onChange, legacyDomExport } = options;
  const byElementId = new Map<number, WidgetInstanceCapabilities>();
  const byWidgetId = new Map<string, WidgetInstanceCapabilities>();

  const notify = () => onChange?.();

  const registerInstance = (capabilities: WidgetInstanceCapabilities): void => {
    byElementId.set(capabilities.dashboardElementId, capabilities);
    byWidgetId.set(capabilities.widgetId, capabilities);
    notify();
  };

  const unregisterInstance = (dashboardElementId: number): void => {
    const existing = byElementId.get(dashboardElementId);
    if (existing) {
      byWidgetId.delete(existing.widgetId);
    }
    byElementId.delete(dashboardElementId);
    notify();
  };

  const getInstance = (
    dashboardElementId: number
  ): WidgetInstanceCapabilities | undefined =>
    byElementId.get(dashboardElementId);

  const getInstanceByWidgetId = (
    widgetId: string
  ): WidgetInstanceCapabilities | undefined => byWidgetId.get(widgetId);

  const hasInteractiveReportExportsForElements = (
    elements: ReportElementType[]
  ): boolean => {
    const leaves = flattenExportableElements(elements);

    return leaves.some((element) => {
      if (element.element_type === 'collection') {
        return false;
      }

      const caps = getInstanceByWidgetId(element.template_variable);
      const reportExport = caps?.reportExports?.find(
        (exp) => exp.format === element.export_type
      );

      return reportExport?.supportsInteractive === true;
    });
  };

  const gatherReportExports = async (
    elements: ReportElementType[],
    mode: ReportExportMode,
    gatherOptions: ReportGatherOptions = {}
  ): Promise<ReportWidgetExportResult[]> => {
    const leaves = flattenExportableElements(elements);
    const totalCount = leaves.length;
    const resolveDisplayName =
      gatherOptions.resolveDisplayName ??
      ((element) =>
        resolveReportExportDisplayName(element, {
          getWidgetByTemplateVariable: () => undefined,
        }));
    const results: ReportWidgetExportResult[] = [];
    let completedCount = 0;

    const emitGatheringProgress = (
      currentElement?: ReportElementType,
      currentDisplayName?: string
    ) => {
      gatherOptions.onProgress?.({
        phase: 'gathering',
        mode,
        completedCount,
        totalCount,
        currentElement,
        currentDisplayName,
      });
    };

    const exportLeaf = async (
      element: ReportElementType
    ): Promise<ReportWidgetExportResult> => {
      const caps = getInstanceByWidgetId(element.template_variable);
      const exportType =
        element.element_type === 'collection' ? undefined : element.export_type;
      const reportExport = caps?.reportExports?.find(
        (exp) => exp.format === exportType
      );

      let result: string | null | undefined;

      if (reportExport) {
        result = await reportExport.export({
          mode,
          signal: gatherOptions.signal,
        });
      } else if (legacyDomExport) {
        result = await legacyDomExport(
          element,
          gatherOptions.legacyExportContext ?? {}
        );
      }

      if (
        result === null &&
        mode === 'interactive' &&
        gatherOptions.onExportCancelled
      ) {
        const displayName = resolveDisplayName(element);
        const decision = await gatherOptions.onExportCancelled(
          element,
          displayName
        );
        if (decision === 'abort') {
          throw new Error('Report export cancelled by user');
        }
      }

      return { element, result: result ?? undefined };
    };

    try {
      if (mode === 'headless') {
        emitGatheringProgress();
        const settled = await Promise.all(
          leaves.map(async (element) => {
            const row = await exportLeaf(element);
            completedCount += 1;
            emitGatheringProgress();
            return row;
          })
        );
        results.push(...settled);
      } else {
        for (let index = 0; index < leaves.length; index += 1) {
          const element = leaves[index];
          const displayName = resolveDisplayName(element);
          emitGatheringProgress(element, displayName);
          const row = await exportLeaf(element);
          results.push(row);
          completedCount += 1;
          emitGatheringProgress();
        }
      }

      gatherOptions.onProgress?.({ phase: 'done' });
      return results;
    } catch (error) {
      gatherOptions.onProgress?.({ phase: 'error', error });
      throw error;
    }
  };

  return {
    registerInstance,
    unregisterInstance,
    getInstance,
    getInstanceByWidgetId,
    gatherReportExports,
    hasInteractiveReportExportsForElements,
  };
}
